import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import conferenceService from '../../services/conferenceService';

import reviewCriteriaService from '../../services/reviewCriteriaService';
import StatCard from './components/StatCard';
import SubmissionTable from './components/SubmissionTable';

const SubmissionTrackingDashboard = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('submissions');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Accepts both spellings. The app used ?id= on six routes and
  // ?conferenceId= on three, so links between them only worked if you knew
  // which one each page wanted. Everything now *emits* ?conferenceId=;
  // reading ?id= as well keeps already-shared and bookmarked links alive.
  const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conference, setConference] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [trackFilter, setTrackFilter] = useState('all');
  const [tracks, setTracks] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (conferenceId) {
      loadDashboardData();
    }
  }, [user, conferenceId, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [confData, tracksData, submissionsData] = await Promise.all([
        conferenceService?.getById(conferenceId),
        conferenceService?.getTracks(conferenceId),
        conferenceService?.getSubmissions(conferenceId)
      ]);

      setConference(confData);
      setTracks(tracksData);
      setSubmissions(submissionsData);

      if (confData?.id) {
        try {
          const stats = await reviewCriteriaService?.getConferenceReviewStats(confData?.id);
          setReviewStats(stats);
        } catch (statsError) {
          console.error('Error loading review stats:', statsError);
        }
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSubmissions = () => {
    let filtered = submissions;

    if (statusFilter !== 'all') {
      filtered = filtered?.filter(sub => sub?.status === statusFilter);
    }

    if (trackFilter !== 'all') {
      filtered = filtered?.filter(sub => sub?.trackId === trackFilter);
    }

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(sub => 
        sub?.title?.toLowerCase()?.includes(query) ||
        sub?.author?.firstName?.toLowerCase()?.includes(query) ||
        sub?.author?.lastName?.toLowerCase()?.includes(query) ||
        sub?.author?.email?.toLowerCase()?.includes(query)
      );
    }

    return filtered;
  };

  const getStatusCounts = () => {
    const counts = {
      total: submissions?.length || 0,
      unassigned: 0,
      awaiting_review: 0,
      under_review: 0,
      completed: 0
    };

    submissions?.forEach(sub => {
      if (sub?.status === 'submitted') {
        counts.awaiting_review++;
      } else if (sub?.status === 'under_review') {
        counts.under_review++;
      } else if (sub?.status === 'accepted' || sub?.status === 'rejected') {
        counts.completed++;
      }
    });

    return counts;
  };

  const handleViewSubmission = (submission) => {
    navigate(`/paper-review-interface?submissionId=${submission?.id}`);
  };

  const handleExportData = () => {
    toastSuccess(t('trackingDashboard.exportNotice'));
  };

  const statusCounts = getStatusCounts();
  const filteredSubmissions = getFilteredSubmissions();

  const statusOptions = [
    { value: 'all', label: t('trackingDashboard.submissionsTable.statusFilter.allStatus') },
    { value: 'submitted', label: t('trackingDashboard.submissionsTable.statusFilter.submitted') },
    { value: 'under_review', label: t('trackingDashboard.submissionsTable.statusFilter.underReview') },
    { value: 'accepted', label: t('trackingDashboard.submissionsTable.statusFilter.accepted') },
    { value: 'rejected', label: t('trackingDashboard.submissionsTable.statusFilter.rejected') },
    { value: 'revision_required', label: t('trackingDashboard.submissionsTable.statusFilter.revisionRequired') }
  ];

  const trackOptions = [
    { value: 'all', label: t('trackingDashboard.submissionsTable.trackFilter.allTracks') },
    ...tracks?.map(track => ({ value: track?.id, label: track?.name })) || []
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('trackingDashboard.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <UniversalNavbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error mb-4">{error}</p>
            <Button onClick={() => navigate('/conference-management-dashboard?id=' + conferenceId)}>
              {t('trackingDashboard.backToConference')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <div className="page-shell py-8">
        <div className="bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground">
                {t('trackingDashboard.title')}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {conference?.name || 'Conference'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/conference-management-dashboard?id=' + conferenceId)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                {t('trackingDashboard.back')}
              </Button>
              <Button
                onClick={handleExportData}
                iconName="Download"
                iconPosition="left"
              >
                {t('trackingDashboard.export')}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="page-shell px-4 md:px-6 lg:px-8 py-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title={t('trackingDashboard.stats.totalSubmissions')}
                value={statusCounts?.total}
                icon="FileText"
                color="blue"
              />
              <StatCard
                title={t('trackingDashboard.stats.awaitingReview')}
                value={statusCounts?.awaiting_review}
                icon="Clock"
                color="yellow"
              />
              <StatCard
                title={t('trackingDashboard.stats.underReview')}
                value={statusCounts?.under_review}
                icon="Eye"
                color="purple"
              />
              <StatCard
                title={t('trackingDashboard.stats.reviewComplete')}
                value={statusCounts?.completed}
                icon="CheckCircle"
                color="green"
              />
            </div>

            {reviewStats && (
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">{t('trackingDashboard.reviewProgress.title')}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">{t('trackingDashboard.reviewProgress.completionRate')}</span>
                      <span className="text-sm font-medium text-foreground">{reviewStats?.completionRate}%</span>
                    </div>
                    <div className="w-full bg-muted h-2">
                      <div
                        className="bg-primary h-2 transition-all duration-300"
                        style={{ width: `${reviewStats?.completionRate}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-foreground">{reviewStats?.totalSubmissions}</p>
                      <p className="text-xs text-muted-foreground">{t('trackingDashboard.reviewProgress.total')}</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-success">{reviewStats?.reviewedSubmissions}</p>
                      <p className="text-xs text-muted-foreground">{t('trackingDashboard.reviewProgress.reviewed')}</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-accent">{reviewStats?.pendingReviews}</p>
                      <p className="text-xs text-muted-foreground">{t('trackingDashboard.reviewProgress.pending')}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold text-foreground">{t('trackingDashboard.submissionsTable.title')}</h3>
                <div className="flex flex-col md:flex-row gap-3">
                  <Input
                    type="text"
                    placeholder={t('trackingDashboard.submissionsTable.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="md:w-64"
                  />
                  <Select
                    value={statusFilter}
                    onChange={(value) => setStatusFilter(value)}
                    options={statusOptions}
                  />
                  <Select
                    value={trackFilter}
                    onChange={(value) => setTrackFilter(value)}
                    options={trackOptions}
                  />
                </div>
              </div>

              <SubmissionTable
                submissions={filteredSubmissions}
                onViewSubmission={handleViewSubmission}
                filtered={Boolean(searchQuery?.trim() || statusFilter !== 'all' || trackFilter !== 'all')}
                onClearFilters={() => { setSearchQuery(''); setStatusFilter('all'); setTrackFilter('all'); }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionTrackingDashboard;